import { NavLink, Outlet } from 'react-router-dom';
import { AppFooter } from '../components/AppFooter';
import { useSystemAuth } from './SystemAuthContext';

/**
 * The console shell.
 *
 * STYLED LIKE THE REST OF THE APP, on purpose and after getting it wrong
 * once. The first version hand-picked bg-slate-950 / bg-slate-900 to look
 * "operator-ish" - but index.css INVERTS the slate scale under .dark
 * (--color-slate-900 is #e7edf4, nearly white), so those classes rendered
 * light on a dark page and the console looked broken. Using the same
 * classes as every other page means the palette does the work and both
 * themes are correct for free.
 *
 * Its own layout rather than AppLayout: the module rail, the notification
 * bell and the assistant all belong to a tenant session that does not
 * exist here. The footer IS shared - dark mode and the utilities are
 * app-wide, not tenant-specific.
 */

const TABS = [
  { to: '/system/organisations', label: 'Organisations' },
  { to: '/system/operators', label: 'Operators' },
  { to: '/system/settings', label: 'Platform settings' },
  { to: '/system/audit', label: 'Audit' },
];

export function SystemLayout() {
  const { admin, signOut } = useSystemAuth();

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <header className="shrink-0 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-baseline gap-3">
            <span className="text-sm font-semibold text-slate-900">Platform console</span>
            {/* The one visual reminder that this session is not inside any
                organisation. Amber rather than brand colour so it reads as
                a caution, not a feature. */}
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
              cross-organisation
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-slate-500">{admin?.email}</span>
            <button type="button" onClick={signOut}
              className="rounded-lg border border-slate-300 px-2.5 py-1 font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
              Sign out
            </button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-6xl gap-1 px-6">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to}
              className={({ isActive }) =>
                [
                  '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition',
                  isActive
                    ? 'border-brand-500 text-brand-700'
                    : 'border-transparent text-slate-500 hover:text-slate-900',
                ].join(' ')
              }>
              {t.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 overflow-y-auto px-6 py-6">
        <Outlet />
      </main>

      {/*
        The same footer, told what it is. Settings points at the console's
        own platform settings - '/settings' is a tenant route and would
        bounce an operator to the portal login - and the assistant is off,
        because it calls tenant endpoints with a token this session does
        not have.
      */}
      <AppFooter settingsTo="/system/settings" showAssistant={false} />
    </div>
  );
}

// --------------------------------------------------- small shared pieces ---

export function Panel({ title, action, children }: {
  title: string; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert"
       className="mb-4 whitespace-pre-line rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </p>
  );
}

export function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export const inputClass =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none '
  + 'placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 '
  + 'disabled:bg-slate-50 disabled:text-slate-500';

export const buttonClass =
  'rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition '
  + 'hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 '
  + 'disabled:cursor-not-allowed disabled:bg-slate-300';

export const secondaryButtonClass =
  'rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition '
  + 'hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400';

export const tableHeadClass =
  'border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500';
